import { Button, Input, InlineMessage } from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { fetchSplit, type ResourceRole, type ResourceSplit, type RoleShare, saveSplit } from './resourcingApi';
import { splitRows, splitTotal } from './split';

interface Props {
  opportunityId: string;
  roles: ResourceRole[];
  /** After a save or reset, so the planner reloads the reshaped forecast. */
  onChanged: () => void;
}

const LABEL = 'border-0 border-b border-solid border-[var(--rule)] pb-1.5 text-[11px] font-emphasis uppercase tracking-[var(--collabel-tracking)] text-[var(--fg-muted)]';

/** A deal's role split: what was sold, what it is planned at now, and the actual split people set. */
export function RoleSplitCard({ opportunityId, roles, onChanged }: Props): JSX.Element | null {
  const [split, setSplit] = useState<ResourceSplit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setSplit(null);
    setDraft(null);
    setError(null);
    fetchSplit(authenticatedFetch, opportunityId)
      .then((s) => live && setSplit(s))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load the role split.'));
    return () => {
      live = false;
    };
  }, [opportunityId]);

  if (!split) {
    return error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : null;
  }
  const rows = splitRows(split, roles);
  if (!rows.length) return null;
  const total = draft ? splitTotal(draft) : 100;

  const save = async (shares: RoleShare[] | null) => {
    setBusy(true);
    setError(null);
    try {
      setSplit(await saveSplit(authenticatedFetch, opportunityId, shares));
      setDraft(null);
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save the role split.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className={LABEL}>Role split</div>
      <div className="flex items-center gap-2 py-2 text-xs text-[var(--fg-muted)]">
        <div className="min-w-0 flex-1">Role</div>
        <div className="w-16 flex-none text-right">Sold</div>
        <div className="w-16 flex-none text-right">Actual</div>
        <div className="w-20 flex-none text-right">Planned</div>
      </div>
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-2 border-0 border-b border-solid border-[var(--border-subtle)] py-2">
          <div className="min-w-0 flex-1 font-emphasis text-[var(--fg-strong)]">{r.name}</div>
          <div className="w-16 flex-none text-right tabular-nums">{r.sold === null ? '–' : `${r.sold}%`}</div>
          <div className="w-16 flex-none text-right tabular-nums">
            {draft ? (
              <Input
                type="number"
                min={0}
                max={100}
                step="1"
                aria-label={`${r.name} actual share`}
                value={draft[r.id] ?? ''}
                onChange={(e) => setDraft({ ...draft, [r.id]: e.target.value })}
              />
            ) : r.actual === null ? '–' : `${r.actual}%`}
          </div>
          <div className="w-20 flex-none text-right tabular-nums text-[var(--fg-muted)]">
            <div>{`${r.plannedPct}%`}</div>
            <div className="text-xs">{`${r.plannedHours}h`}</div>
          </div>
        </div>
      ))}
      <div className="py-2 text-xs text-[var(--fg-muted)]">
        {draft
          ? `Total ${total}%. Unnamed forecast hours are split this way; hours people hold stay as they are.`
          : split.actual
            ? 'Actual split set for this deal. Regenerating the forecast keeps it.'
            : 'Using the sold split from the products on the quote.'}
      </div>
      {error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : null}
      <div className="flex flex-wrap gap-2">
        {draft ? (
          <>
            <Button size="sm" variant="brand" disabled={busy || total !== 100} onClick={() => void save(rows.map((r) => ({ resource_role_id: r.id, share_pct: Number(draft[r.id] || 0) })))}>
              Save split
            </Button>
            <Button size="sm" disabled={busy} onClick={() => setDraft(null)}>Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" disabled={busy} onClick={() => setDraft(Object.fromEntries(rows.map((r) => [r.id, r.actual === null ? '' : String(r.actual)])))}>
              Change split
            </Button>
            {split.actual ? (
              <Button size="sm" disabled={busy} onClick={() => void save(null)}>Reset to sold split</Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
